import { useTeam } from '@vrplatform/data';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { formatCurrency, hasValue } from '@vrplatform/utils';
import { useMantineTheme } from '@mantine/core';
import { useMemo } from 'react';
import type {
  BalanceSheetRow,
  BalanceSheetTableData,
} from './useBalanceSheetTableQuery';
import { useBalanceSheetTableFilter } from './useBalanceSheetTableFilter';
import { getBalanceSheetTableCellStyle } from './_styles';
import {
  BalanceSheetTableCell,
  type BalanceSheetJournalEntriesArgs,
} from './BalanceSheetTableCell';

type Column = MRT_ColumnDef<BalanceSheetRow>;

const isColumnZero = (rows: BalanceSheetRow[], columnId: string): boolean => {
  return rows.every(
    (row) =>
      (row.values[columnId]?.total ?? 0) === 0 &&
      isColumnZero(row.subRows ?? [], columnId)
  );
};

export const useBalanceSheetTableColumns = (
  initialColumns: BalanceSheetTableData['columns'],
  rows: BalanceSheetTableData['rows'],
  onOpenJournalEntries?: (args: BalanceSheetJournalEntriesArgs) => void
) => {
  const theme = useMantineTheme();
  const [{ currency }] = useTeam();
  const { filter } = useBalanceSheetTableFilter();

  const hideZeroColumns = useMemo(() => {
    return filter.view?.includes('non-zero-columns');
  }, [filter.view]);

  return useMemo<Column[]>(() => {
    const titleColumn: Column = {
      header: 'Title',
      Header: '',
      id: 'title',
      accessorKey: 'title',
      minSize: 300,
      maxSize: 0,
      size: 300,
      mantineTableBodyCellProps: (props) => ({
        sx: getBalanceSheetTableCellStyle(theme, {
          rowDepth: props.row.depth,
          type: props.row.original.type,
        }),
      }),
      Cell: ({ row, cell }) => {
        return (
          <BalanceSheetTableCell.Base
            depth={row.depth}
            type={row.original.type}
            align="left"
            size={row.depth === 0 ? 'md' : 'sm'}
            breakWord
          >
            {cell.getValue<string | number>()}
          </BalanceSheetTableCell.Base>
        );
      },
    };

    // const base: Column[] = [
    //   titleColumn,
    //   // {
    //   //   header: 'Total',
    //   //   id: 'total',
    //   //   mantineTableHeadCellProps: {
    //   //     align: 'right',
    //   //     sx: (theme) => ({
    //   //       fontWeight: 400,
    //   //       color: theme.colors.gray[7],
    //   //       textAlign: 'right',
    //   //     }),
    //   //   },
    //   //   mantineTableBodyCellProps: (props) => ({
    //   //     align: 'right',
    //   //     sx: {
    //   //       textAlign: 'right',
    //   //       ...getBalanceSheetTableCellStyle(theme, {
    //   //         rowDepth: props.row.depth,
    //   //         type: props.row.original.type,
    //   //       }),
    //   //     },
    //   //   }),
    //   //   accessorFn: (row) => {
    //   //     if (row.total === undefined) return '';

    //   //     return formatCurrency((row.total ?? 0) / 100, currency);
    //   //   },
    //   //   Cell: ({ cell, row }) => {
    //   //     const openModal = useBalanceSheetTableJournalEntriesModal();

    //   //     const onClick = useMemo<(() => void) | undefined>(() => {
    //   //       if (row.original.type === 'account') {
    //   //         return () => {
    //   //           openModal({
    //   //             accountId: row.original.id,
    //   //           });
    //   //         };
    //   //       }

    //   //       if (row.original.type === 'category') {
    //   //         return () => {
    //   //           openModal({
    //   //             categoryId: row.original.id,
    //   //           });
    //   //         };
    //   //       }

    //   //       return undefined;
    //   //     }, [row.original.id, row.original.type, openModal]);

    //   //     return (
    //   //       <BalanceSheetTableCell.Base
    //   //         depth={row.depth}
    //   //         align="right"
    //   //         type={row.original.type}
    //   //         onClick={onClick}
    //   //       >
    //   //         {cell.getValue<string | number>()}
    //   //       </BalanceSheetTableCell.Base>
    //   //     );
    //   //   },
    //   // },
    // ];

    if (initialColumns) {
      const columns = [...initialColumns];

      return [
        titleColumn,
        ...columns
          .map<Column | undefined>((column) => {
            if (hideZeroColumns && isColumnZero(rows, column.id)) {
              return undefined;
            }

            return {
              header: column.title,
              id: column.id,
              mantineTableHeadCellProps: {
                align: 'right',
                sx: (theme) => ({
                  fontWeight: 400,
                  color: theme.colors.gray[7],
                  textAlign: 'right',
                }),
              },
              mantineTableBodyCellProps: (props) => ({
                align: 'right',
                sx: {
                  textAlign: 'right',
                  ...getBalanceSheetTableCellStyle(theme, {
                    rowDepth: props.row.depth,
                    type: props.row.original.type,
                  }),
                },
              }),
              accessorFn: (row) => {
                const value = row.values[column.id];

                if (value === undefined) return formatCurrency(0, currency);

                return formatCurrency(
                  (row.values[column.id].total ?? 0) / 100,
                  currency
                );
              },
              Cell: (props) => (
                <BalanceSheetTableCell.Number
                  row={props.row.original}
                  depth={props.row.depth}
                  columndId={column.id}
                  onOpenJournalEntries={onOpenJournalEntries}
                >
                  {props.cell.getValue<string | number>()}
                </BalanceSheetTableCell.Number>
              ),
            };
          })
          .filter(hasValue),
        // base[1],
      ];
    }

    return [];
  }, [
    currency,
    theme,
    hideZeroColumns,
    initialColumns,
    onOpenJournalEntries,
    rows,
  ]);
};
